/* ==============================================================================
   Sessão no cliente
   ==============================================================================
   O access token vive em MEMÓRIA. Nunca em localStorage nem sessionStorage.

   Motivo: qualquer XSS lê `localStorage` trivialmente, e um token lá vazaria
   junto. Em memória ele morre com a aba, e a sessão é restaurada pelo cookie de
   refresh — que é `httpOnly` e JavaScript não alcança. Esse é o par: o segredo
   de longa duração fica fora do alcance do script, e o de curta duração fica
   fora do disco.

   Consequência aceita: recarregar a página exige um `POST /auth/refresh` antes
   de renderizar. É uma requisição, e resolve a causa pela qual "login não
   carregava a página do app" — não havia restauração de sessão nenhuma.
   ============================================================================== */

import { planRenewal } from '@concord/shared';

export interface CurrentUser {
  id: string;
  email: string;
  username: string;
  displayName: string | null;
  pronouns: string | null;
  bio: string | null;
  status: string;
  locale: string;
  timezone: string;
  mfaEnabled: boolean;
  instanceAdmin: boolean;
  emailVerified: boolean;
  createdAt: string;
}

interface TokenState {
  accessToken: string;
  /** Instante de expiração em ms. Usado para renovar ANTES de falhar. */
  expiresAt: number;
}

let token: TokenState | null = null;

/**
 * Renovação em voo, compartilhada.
 *
 * Sem isto, várias requisições simultâneas com token expirado disparariam
 * vários `POST /auth/refresh` em paralelo. Como o refresh ROTACIONA o token, a
 * segunda chamada apresentaria um token já rotacionado — o que o servidor trata,
 * corretamente, como REUSO e revoga a sessão inteira.
 *
 * Ou seja: sem deduplicar, abrir o app com o token expirado deslogaria o
 * usuário. A promise compartilhada garante uma única rotação.
 */
/*
 * A promise compartilhada mora em `refreshDetailedInFlight`, logo abaixo de
 * `refreshDetailed`. Este comentário fica aqui porque é onde se procura pela
 * explicação — a dedupe é o que impede "abrir o app desloga o usuário".
 */

/** Renova com folga, para não perder uma requisição na virada. */
const RENEW_MARGIN_MS = 30_000;

function setToken(accessToken: string, expiresInSeconds: number): void {
  token = {
    accessToken,
    expiresAt: Date.now() + expiresInSeconds * 1000,
  };
}

export function clearSession(): void {
  token = null;
  /*
   * A promise em voo NÃO é anulada aqui.
   *
   * `clearSession` é chamada de DENTRO de `refreshDetailed` quando o servidor
   * responde 401. Anular a referência ali faria o `finally` daquela mesma
   * execução tentar limpar algo que já foi limpo — inofensivo — mas o risco real
   * é o inverso: uma segunda chamada concorrente veria `null`, dispararia um
   * refresh NOVO com o cookie já rotacionado, e o servidor leria isso como
   * reuso, revogando a família inteira de tokens.
   *
   * O `finally` de `refreshDetailed` é o único lugar que zera a promise, e ele
   * roda exatamente uma vez por execução.
   */
}

function isTokenUsable(): boolean {
  return token !== null && token.expiresAt - RENEW_MARGIN_MS > Date.now();
}

/**
 * Troca o cookie de refresh por um access token novo.
 *
 * Devolve `false` quando não há sessão — que é o caso normal de primeiro acesso,
 * não um erro. Por isso não lança.
 */
async function refresh(): Promise<boolean> {
  return (await refreshDetailed()) === 'ok';
}

/**
 * Quanto tempo uma tentativa de renovação pode levar antes de ser abortada.
 *
 * O DEFEITO que isto corrige, e ele deixava o app INACESSÍVEL
 * ==========================================================
 * `refreshDetailed` fazia `fetch` sem `AbortController`. Um `fetch` que nunca
 * responde — proxy travado, túnel morto, rede que engole o pacote sem RST — não
 * rejeita: ele fica pendente para sempre.
 *
 * E `restoreSession()` espera por ele no BOOT. Então:
 *
 *     booting = true
 *       └─ await Promise.all([checkBackend(), restoreSession()])
 *                                             └─ await refresh()  <- pendurado
 *
 * `booting` nunca virava `false`, e a tela ficava no `BootScreen`: preto com a
 * palavra "Concord" e mais nada. Sem erro, sem formulário de login, sem
 * caminho de saída além de recarregar — que fazia a mesma coisa.
 *
 * `checkBackend()` já tinha `AbortController` de 4s; o refresh não tinha
 * nenhum. A assimetria é o defeito: a tela de boot esperava a mais lenta das
 * duas, e uma delas não tinha limite.
 */
const REFRESH_TIMEOUT_MS = 6_000;

interface RefreshOptions {
  /**
   * Quantas tentativas. Padrão 3.
   *
   * No BOOT usa-se 1: a pessoa está olhando uma tela preta, e três tentativas
   * com espera entre elas somam mais de vinte segundos de nada. Melhor mostrar
   * o login e deixá-la decidir — se a sessão existia, entrar de novo custa um
   * clique no gerenciador de senha; encarar tela preta não custa nada menos.
   */
  attempts?: number;
  timeoutMs?: number;
}

/** Ver o comentário longo no topo do arquivo sobre por que isto é obrigatório. */
let refreshDetailedInFlight: Promise<RefreshOutcome> | null = null;

/**
 * Resultado de uma tentativa de renovação.
 *
 * `expired`     a sessão realmente acabou. Tem de pedir a senha.
 * `unavailable` o servidor não respondeu ou respondeu que era demais. A sessão
 *               pode estar perfeitamente boa; a resposta certa é ESPERAR.
 */
type RefreshOutcome = 'ok' | 'expired' | 'unavailable';

/**
 * Renova o access token, distinguindo "expirou" de "não deu para saber".
 *
 * O DEFEITO que isto corrige
 * ==========================
 * A versão anterior era:
 *
 *     if (!response.ok) { clearSession(); return false; }
 *
 * Qualquer resposta não-2xx apagava a sessão. Inclusive **429**, inclusive 502
 * de um proxy reiniciando, inclusive falha de rede num túnel instável. O
 * usuário via "Sua sessão expirou. Entre novamente." e digitava a senha — por
 * nada, porque a sessão nunca havia expirado.
 *
 * Foi exatamente o que aconteceu em produção: o `/auth/refresh` NÃO envia
 * `Authorization` (a credencial dele é o cookie), então o limitador de taxa o
 * classifica por IP. Um vizinho ruidoso no mesmo IP público — no caso, uma
 * bateria de testes automatizados saindo pela mesma conexão — drenou o balde, e
 * o navegador foi deslogado por um 429.
 *
 * A distinção que importa: 401 e 403 são o servidor dizendo "esta credencial
 * não vale". 429 e 5xx são o servidor dizendo "não agora". Tratar os dois
 * grupos igual transforma uma indisponibilidade momentânea em perda de sessão.
 */
async function refreshDetailed(
  options: RefreshOptions = {},
): Promise<RefreshOutcome> {
  if (refreshDetailedInFlight) return refreshDetailedInFlight;

  const maxAttempts = options.attempts ?? 3;
  const timeoutMs = options.timeoutMs ?? REFRESH_TIMEOUT_MS;

  refreshDetailedInFlight = (async () => {
    try {
      /*
       * Tentativas com espera crescente, cada uma com PRAZO.
       *
       * Uma só transformaria um 429 momentâneo em sessão perdida na prática,
       * porque quem chama trata `unavailable` como "não consegui entrar". Três
       * cobrem uma janela de limitação curta — mas quem chama do boot pede 1,
       * porque lá o custo de esperar é uma tela preta.
       */
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        /*
         * `AbortController` por tentativa, e não um para todas.
         *
         * Compartilhar o controller faria a primeira expiração abortar as
         * tentativas seguintes antes de elas começarem — o retry existiria no
         * código e não na prática.
         */
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);

        const response = await fetch('/api/v1/auth/refresh', {
          method: 'POST',
          // Sem `content-type`: não há corpo, e declará-lo sem corpo fazia o
          // Fastify rejeitar com FST_ERR_CTP_EMPTY_JSON_BODY.
          credentials: 'same-origin',
          signal: controller.signal,
        })
          .catch(() => null)
          .finally(() => clearTimeout(timer));

        /* ---- Sem resposta: rede caiu, proxy reiniciando ---- */
        if (response === null) {
          if (attempt < 2) {
            await sleep(backoffMs(attempt, null));
            continue;
          }
          return 'unavailable';
        }

        /* ---- A credencial não vale: aqui SIM a sessão acabou ---- */
        if (response.status === 401 || response.status === 403) {
          clearSession();
          return 'expired';
        }

        /* ---- "Não agora": espera e tenta de novo ---- */
        if (response.status === 429 || response.status >= 500) {
          if (attempt < 2) {
            /*
             * `Retry-After` é respeitado quando vem.
             *
             * O servidor sabe quanto falta para a janela virar; adivinhar com
             * backoff cego tentaria antes da hora e gastaria a tentativa.
             */
            await sleep(backoffMs(attempt, response.headers.get('retry-after')));
            continue;
          }
          return 'unavailable';
        }

        if (!response.ok) {
          /*
           * Outro 4xx é defeito de contrato, não sessão expirada.
           *
           * Não apaga a sessão: um 400 por mudança de formato de requisição
           * deslogaria todo mundo no deploy, e o token em memória pode ainda
           * estar valendo.
           */
          return 'unavailable';
        }

        const body = (await response.json().catch(() => null)) as {
          accessToken?: string;
          expiresIn?: number;
        } | null;

        if (!body?.accessToken || typeof body.expiresIn !== 'number') {
          // 200 com corpo inválido é defeito do servidor, não sessão expirada.
          return 'unavailable';
        }

        setToken(body.accessToken, body.expiresIn);
        return 'ok';
      }

      return 'unavailable';
    } finally {
      refreshDetailedInFlight = null;
    }
  })();

  return refreshDetailedInFlight;
}

/**
 * Espera antes da próxima tentativa.
 *
 * Usa `Retry-After` quando o servidor o envia — ele aceita segundos ou uma data
 * HTTP, e as duas formas aparecem na prática. O teto de 8 s existe para o boot
 * da aplicação não ficar pendurado: um limite de 10 minutos não se espera, se
 * reporta.
 */
function backoffMs(attempt: number, retryAfter: string | null): number {
  if (retryAfter !== null) {
    const seconds = Number(retryAfter);
    if (Number.isFinite(seconds) && seconds > 0) {
      return Math.min(seconds * 1000, 8_000);
    }
    const date = Date.parse(retryAfter);
    if (Number.isFinite(date)) {
      return Math.min(Math.max(0, date - Date.now()), 8_000);
    }
  }
  /*
   * Backoff exponencial com JITTER.
   *
   * Sem o jitter, várias abas da mesma pessoa (ou vários clientes atingidos pela
   * mesma janela) tentariam de novo no mesmo instante e reproduziriam o 429 que
   * estavam esperando passar.
   */
  const base = 400 * 2 ** attempt;
  return base + Math.random() * base;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export { refreshDetailed };

/** Guarda o par recebido de `/auth/login` ou `/auth/register`. */
export function adoptSession(accessToken: string, expiresInSeconds: number): void {
  setToken(accessToken, expiresInSeconds);
}

/**
 * Devolve um access token utilizável, renovando se preciso.
 *
 * É o `TokenProvider` que `@concord/core` espera — tanto o `ApiClient` quanto o
 * `GatewayClient` consomem esta função. Um lugar só decide "o token está bom?" e
 * "renove", e é por isso que a deduplicação de refresh acima protege os dois
 * canais: HTTP e WebSocket compartilham a mesma promise em voo.
 *
 * Devolve `null` quando não há sessão. Não lança: ausência de sessão é o estado
 * normal de quem não entrou.
 */
export async function getAccessToken(): Promise<string | null> {
  if (!isTokenUsable() && !(await refresh())) return null;
  return token?.accessToken ?? null;
}

/**
 * Restaura a sessão no carregamento da página.
 *
 * Devolve o usuário autenticado, ou `null` se não há sessão. É o que faz o
 * recarregamento cair na tela do app em vez de voltar para o login.
 */
export async function restoreSession(): Promise<CurrentUser | null> {
  /*
   * UMA tentativa, com prazo curto — e o motivo é a tela preta.
   *
   * Esta função roda no boot, dentro do `Promise.all` que mantém `booting`
   * verdadeiro. Cada segundo aqui é um segundo de `BootScreen`. Três tentativas
   * com backoff somariam mais de vinte segundos mostrando só a palavra
   * "Concord" — indistinguível de um app quebrado.
   *
   * Falhar rápido e mostrar o login é a degradação certa: se a sessão existia,
   * o gerenciador de senha resolve num clique. Se o servidor estava fora, a
   * pessoa vê a pílula de "API indisponível" em vez de nada.
   */
  const outcome = await refreshDetailed({ attempts: 1, timeoutMs: 5_000 });
  if (outcome !== 'ok' && !isTokenUsable()) return null;
  return fetchCurrentUser();
}

export async function fetchCurrentUser(): Promise<CurrentUser | null> {
  const response = await authorizedFetch('/api/v1/users/@me');
  if (!response || !response.ok) return null;
  return (await response.json()) as CurrentUser;
}

/**
 * `fetch` com o access token, renovando quando necessário.
 *
 * Duas camadas de renovação, e as duas são necessárias:
 *
 *   ANTES  se o token está perto de expirar, renova preventivamente. Evita uma
 *          requisição que falharia por causa de um segundo de diferença.
 *   DEPOIS se ainda assim vier 401, renova UMA vez e repete. Cobre o caso de o
 *          token ter sido invalidado do lado do servidor (troca de senha,
 *          revogação de sessão), que a checagem de tempo não prevê.
 *
 * A repetição é única de propósito: repetir em laço num 401 persistente
 * produziria requisição infinita.
 */
export async function authorizedFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response | null> {
  if (!isTokenUsable() && !(await refresh())) return null;
  if (!token) return null;

  const send = (accessToken: string) =>
    fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: {
        ...init.headers,
        authorization: `Bearer ${accessToken}`,
      },
    });

  let response = await send(token.accessToken);

  if (response.status === 401) {
    if (!(await refresh()) || !token) return null;
    response = await send(token.accessToken);
  }

  return response;
}

/** Encerra a sessão no servidor e limpa o estado local. */
export async function logout(): Promise<void> {
  try {
    await fetch('/api/v1/auth/logout', {
      method: 'POST',
      credentials: 'same-origin',
    });
  } finally {
    // Limpa mesmo se a chamada falhar: manter o token em memória depois de o
    // usuário pedir para sair é pior que uma sessão órfã no servidor, que
    // expira sozinha.
    clearSession();
  }
}

/**
 * Renovação de fundo, agendada para antes da expiração.
 *
 * Sem isto, uma aba aberta por mais de 10 minutos só descobriria o token
 * expirado na próxima ação do usuário — que falharia, renovaria e repetiria,
 * adicionando latência visível exatamente no clique.
 *
 * Devolve a função de cancelamento.
 */
export function startTokenRenewal(
  onLost: (reason: 'expired' | 'unavailable') => void,
): () => void {
  let cancelled = false;
  let timer: number | undefined;

  const schedule = () => {
    if (cancelled || !token) return;

    /* ========================================================================
       O laço que criou 1,17 MILHÃO de sessões
       ========================================================================
       `setTimeout` guarda o atraso num inteiro de 32 bits COM SINAL. O teto é
       2.147.483.647 ms — 24,86 dias. Passar disso não é arredondado nem
       recusado: o valor transborda, vira negativo, e o navegador dispara o
       temporizador IMEDIATAMENTE.

       O token dura 30 dias. 30 dias são 2.592.000.000 ms, que transbordam por
       444 milhões. `2592000000 | 0` é −1702967296.

       O que acontecia então:

         agenda para daqui a 30 dias  →  dispara agora
         renova o token               →  ganha outros 30 dias
         agenda para daqui a 30 dias  →  dispara agora
         ...

       Um laço de renovação limitado só pelo tempo de ida e volta da rede. Cada
       volta ROTACIONA o token de atualização, e rotação grava uma linha nova em
       `app.sessions`. Medido em produção: 677.454 sessões num único dia, para
       13 usuários — e 1.171.788 no total, 929 mil delas ainda válidas.

       Duas consequências que pareciam problemas separados:

         * a lista de usuários do painel de administração "demora uma vida" —
           ela conta sessões ativas por pessoa, e essa contagem varria quase um
           milhão de linhas a cada abertura;

         * o 429 em `/auth/refresh` que derrubava gente. Na época eu atribuí aos
           meus próprios scripts de teste esgotando o balde por IP. Estava
           errado: eram os navegadores dos usuários, girando neste laço.

       O defeito nasceu no dia em que o TTL virou 30 dias, a pedido. Com os 10
       minutos anteriores o atraso cabia folgado nos 32 bits e nada disso
       aparecia — a mudança de configuração foi correta, e revelou um limite que
       o código nunca tinha encostado.

       A correção não é encurtar o token. É dormir em ETAPAS: quando o alvo está
       além do teto, o temporizador só reagenda, sem renovar nada.
    ======================================================================== */
    const plano = planRenewal(token.expiresAt - RENEW_MARGIN_MS, Date.now());

    if (plano.kind === 'wait') {
      timer = window.setTimeout(() => {
        if (!cancelled) schedule();
      }, plano.delayMs);
      return;
    }

    const delay = plano.delayMs;
    timer = window.setTimeout(async () => {
      if (cancelled) return;

      const outcome = await refreshDetailed();
      if (outcome === 'ok') {
        schedule();
        return;
      }

      /*
       * `unavailable` NÃO desiste — reagenda.
       *
       * Antes, qualquer falha de renovação chamava `onLost()`, e o App mostrava
       * "Sua sessão expirou. Entre novamente." Com um 429 ou um proxy
       * reiniciando, isso é uma afirmação FALSA que custa a senha do usuário.
       *
       * O token em memória pode ainda estar valendo (a renovação acontece 30 s
       * antes de expirar). Tentar de novo em 15 s é a resposta certa; avisar
       * quem chamou é só para a interface poder dizer "reconectando" em vez de
       * "entre novamente".
       */
      onLost(outcome);

      if (outcome === 'unavailable' && !cancelled) {
        timer = window.setTimeout(() => {
          if (!cancelled) schedule();
        }, 15_000);
      }
    }, delay);
  };

  schedule();

  return () => {
    cancelled = true;
    if (timer !== undefined) window.clearTimeout(timer);
  };
}
